import { useEffect, useRef, createElement } from "react";
import type { ReactElement } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useRoles } from "@/hooks/useRoles";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { ToastAction } from "@/components/ui/toast";

/**
 * Global realtime listener + polling fallback for new chat messages.
 * Shows a toast notification when a message arrives in any thread the user
 * is part of (as client or coach), unless they are currently viewing the
 * chat page. Tapping the toast navigates to the correct chat page based
 * on thread type (friend → /friends, coaching → /my-coach or /coach).
 */
export function useChatNotifications() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { isCoach } = useRoles();

  // Map thread_id → thread_type for routing
  const threadMapRef = useRef<Map<string, string>>(new Map());
  const senderNamesRef = useRef<Map<string, string>>(new Map());
  const navigateRef = useRef(navigate);
  const isCoachRef = useRef(isCoach);
  // Start polling window 5 minutes in the past to catch messages
  // that arrived just before mount or during realtime reconnection gaps
  const lastSeenAtRef = useRef<string>(
    new Date(Date.now() - 5 * 60 * 1000).toISOString()
  );
  const notifiedIdsRef = useRef<Set<string>>(new Set());

  navigateRef.current = navigate;
  isCoachRef.current = isCoach;

  // Track whether threads have been loaded at least once
  const threadsLoadedRef = useRef(false);

  // Fetch all thread IDs the user participates in (as client or coach)
  const fetchThreads = async () => {
    if (!user) return;
    const { data, error } = await supabase
      .from("message_threads")
      .select("id, coach_id, thread_type")
      .or(`client_id.eq.${user.id},coach_id.eq.${user.id}`);

    if (error) {
      console.error("[ChatNotif] Error fetching threads:", error.message);
      return;
    }

    if (data) {
      const map = new Map<string, string>();
      data.forEach((t) => map.set(t.id, (t as any).thread_type || "coaching"));
      threadMapRef.current = map;
      threadsLoadedRef.current = true;
      console.log("[ChatNotif] Tracking threads:", map.size);
    }
  };

  useEffect(() => {
    if (!user) return;
    fetchThreads();
  }, [user]);

  // Helper to get the correct chat route for a thread
  const getChatRoute = (threadId: string, senderId: string): string => {
    const threadType = threadMapRef.current.get(threadId);
    if (threadType === "friend") return `/friends?chat=${senderId}`;
    return isCoachRef.current ? "/coach" : "/my-coach";
  };

  // Pages where we suppress toast (user is already viewing chat)
  const isChatPage = (): boolean => {
    const path = window.location.pathname;
    return path === "/my-coach" || path === "/coach" || path === "/friends";
  };

  // Helper to show toast for a message
  const showMessageToast = async (msg: {
    id: string;
    thread_id: string;
    sender_id: string;
    body: string;
  }) => {
    // Ignore own messages
    if (!user || msg.sender_id === user.id) {
      console.log("[ChatNotif] Skipped: own message or no user");
      return;
    }

    // Only notify for threads the user is in
    if (!threadMapRef.current.has(msg.thread_id)) {
      console.log("[ChatNotif] Skipped: thread not tracked", msg.thread_id);
      return;
    }

    // Don't toast if user is currently on a chat page
    if (isChatPage()) {
      console.log("[ChatNotif] Skipped: user on chat page");
      return;
    }

    // Skip if already notified
    if (notifiedIdsRef.current.has(msg.id)) {
      console.log("[ChatNotif] Skipped: already notified", msg.id);
      return;
    }
    notifiedIdsRef.current.add(msg.id);

    // Cap the set size to prevent memory leaks
    if (notifiedIdsRef.current.size > 200) {
      const arr = Array.from(notifiedIdsRef.current);
      notifiedIdsRef.current = new Set(arr.slice(-100));
    }

    // Get sender name (cache it)
    let senderName = senderNamesRef.current.get(msg.sender_id);
    if (!senderName) {
      try {
        const { data: profile } = await supabase
          .from("profiles")
          .select("display_name, first_name")
          .eq("user_id", msg.sender_id)
          .maybeSingle();
        senderName =
          profile?.display_name || profile?.first_name || "Someone";
      } catch {
        senderName = "Someone";
      }
      senderNamesRef.current.set(msg.sender_id, senderName);
    }

    const preview =
      msg.body.length > 60 ? msg.body.slice(0, 60) + "…" : msg.body;

    const chatPath = getChatRoute(msg.thread_id, msg.sender_id);

    console.log("[ChatNotif] Showing toast for message from", senderName, "→", chatPath);

    toast({
      title: `💬 ${senderName}`,
      description: preview,
      duration: 5000,
      action: createElement(
        ToastAction,
        {
          altText: "Open chat",
          onClick: () => navigateRef.current(chatPath),
        },
        "Open"
      ) as unknown as ReactElement<typeof ToastAction>,
    });
  };

  // Realtime subscription + polling fallback
  useEffect(() => {
    if (!user) return;

    let channelRef: ReturnType<typeof supabase.channel> | null = null;
    let reconnectTimeout: ReturnType<typeof setTimeout> | null = null;
    let disposed = false;
    let reconnectAttempts = 0;
    const MAX_RECONNECT_ATTEMPTS = 5;

    const createChannel = () => {
      if (disposed) return;

      // Remove previous channel without triggering another reconnect
      if (channelRef) {
        const oldChannel = channelRef;
        channelRef = null; // null out BEFORE removing to prevent callback re-entry
        supabase.removeChannel(oldChannel);
      }

      if (reconnectAttempts >= MAX_RECONNECT_ATTEMPTS) {
        console.log("[ChatNotif] Max reconnect attempts reached, relying on polling only");
        return;
      }

      const channelName = "global-chat-notifications-" + Date.now();
      const newChannel = supabase
        .channel(channelName)
        .on(
          "postgres_changes",
          { event: "INSERT", schema: "public", table: "messages" },
          (payload) => {
            console.log("[ChatNotif] Realtime INSERT received:", payload.new?.id);
            const msg = payload.new as {
              id: string;
              thread_id: string;
              sender_id: string;
              body: string;
            };
            showMessageToast(msg);
          }
        )
        .subscribe((status, err) => {
          console.log("[ChatNotif] Channel status:", status, err || "");
          if (status === "SUBSCRIBED") {
            reconnectAttempts = 0; // reset on successful subscription
          }
          // Only reconnect if this is still the active channel and not disposed
          if ((status === "TIMED_OUT" || status === "CLOSED") && !disposed && channelRef === newChannel) {
            reconnectAttempts++;
            const delay = Math.min(3000 * Math.pow(2, reconnectAttempts - 1), 30000);
            console.log(`[ChatNotif] Channel lost, reconnecting in ${delay / 1000}s (attempt ${reconnectAttempts})`);
            reconnectTimeout = setTimeout(createChannel, delay);
          }
        });

      channelRef = newChannel;
    };

    createChannel();

    // --- Polling function ---
    const doPoll = async () => {
      // If threads haven't loaded yet, fetch them first
      if (!threadsLoadedRef.current || threadMapRef.current.size === 0) {
        await fetchThreads();
      }
      if (threadMapRef.current.size === 0) return;

      const threadIds = Array.from(threadMapRef.current.keys());

      const { data, error } = await supabase
        .from("messages")
        .select("id, body, sender_id, thread_id, created_at")
        .in("thread_id", threadIds)
        .gt("created_at", lastSeenAtRef.current)
        .neq("sender_id", user.id)
        .order("created_at", { ascending: true })
        .limit(10);

      if (error) {
        console.error("[ChatNotif] Poll error:", error.message);
        return;
      }

      if (data && data.length > 0) {
        console.log("[ChatNotif] Poll found", data.length, "new message(s)");
        lastSeenAtRef.current = data[data.length - 1].created_at;

        for (const msg of data) {
          await showMessageToast(msg);
        }
      }
    };

    // Immediate first poll (2s delay to let threads load)
    const initialPollTimeout = setTimeout(doPoll, 2000);

    // Recurring polling fallback (every 15s — reduced from 5s to prevent excessive requests)
    const poll = setInterval(doPoll, 15000);

    return () => {
      disposed = true;
      clearTimeout(initialPollTimeout);
      if (reconnectTimeout) clearTimeout(reconnectTimeout);
      clearInterval(poll);
      if (channelRef) {
        const ch = channelRef;
        channelRef = null;
        supabase.removeChannel(ch);
      }
    };
  }, [user]);
}
